// ==================== LEAD GENERATION SERVICE (AmplifyMsg API proxy) ====================

import { apiService } from './api';
import type { ApiResponse } from '../types';
import type { LeadGenSettingsPayload } from '../pages/customer/lead-generation/settingsMapper';
import type { GenerateContextPayload } from '../pages/customer/lead-generation/generateContextMapper';
import type {
  DiscoveryLeadsApiResponse,
} from '../pages/customer/lead-generation/discoveryLeadsMapper';
import type { PipelineLeadsApiResponse } from '../pages/customer/lead-generation/pipelineLeadsMapper';
import type {
  MeetingApi,
  MeetingsListApiResponse,
} from '../pages/customer/lead-generation/meetingsMapper';

export type LeadGenSettingsResponse = LeadGenSettingsPayload & {
  sandbox_outreach_available?: boolean;
};

/** Shape from GET /api/v1/leads/config (external) proxied via GET /customer/lead-gen/config */
export type LeadGenDiscoveryConfigResponse = {
  config?: GenerateContextPayload;
  context?: GenerateContextPayload;
} & Partial<GenerateContextPayload>;

/** Shape from GET /customer/lead-gen/active-run — the currently-running pipeline, if any. */
export type ActiveRunSnapshot = {
  run_id: string;
  stage: string;
  location?: string | null;
  industries?: string[];
  discovered?: number | null;
  passed?: number | null;
  started_at?: number;
};

export type ActiveRunResponse = {
  active: boolean;
  /** Newest running campaign (kept for older agents). */
  run: ActiveRunSnapshot | null;
  /** Every running campaign, newest first. */
  runs?: ActiveRunSnapshot[];
};

export type DiscoveryLeadsQuery = {
  city?: string;
  industry?: string;
  company_size?: string;
  min_icp_score?: number;
  run_id?: string;
  /** 'yes' = only leads with an email on file, 'no' = only leads missing one. */
  has_email?: 'yes' | 'no';
  /** scored = reached ICP scoring, filtered = dropped before scoring */
  stage?: 'scored' | 'filtered';
  limit?: number;
  offset?: number;
};

export type FindContactsStatus = {
  run_id: string;
  running: boolean;
  total: number;
  processed: number;
  found: number;
  sent: number;
};

export type LeadContactUpdateResult = {
  lead_id: string;
  company_name: string;
  run_id: string;
  contact_email: string;
  current_status: string | null;
  /** sent | skipped | not_applicable | no_sender | no_draft | failed */
  send: string;
};

export type PipelineLeadsQuery = {
  run_id?: string;
  limit?: number;
};

export type PipelineRunsQuery = {
  limit?: number;
  offset?: number;
};

export type RunStatusSummary = {
  total_discovered: number;
  total_enriched: number;
  total_qualified: number;
  total_outreach_drafted: number;
  total_ready_for_review: number;
  total_ready_to_send: number;
  total_contacted: number;
  /** Emails actually delivered (send log) — absent on older agents. */
  total_sent?: number;
  total_replied: number;
  total_meetings: number;
  total_won: number;
  total_lost: number;
};

export type PipelineRunSummary = {
  run_id: string;
  industries: string;
  domain: string | null;
  location: string;
  country: string | null;
  started_at: string;
  completed_at: string | null;
  total_discovered: number;
  total_enriched: number;
  total_evaluated: number;
  total_outreach_drafts: number;
  sandbox_outreach: boolean;
  status_summary: RunStatusSummary;
};

export type PipelineRunsResponse = {
  runs: PipelineRunSummary[];
  total: number;
};

/** A running "Continuous discovery" schedule (re-runs discovery on an interval). */
export type ContinuousSchedule = {
  config_id: string;
  /**
   * starting | running (a run is in progress) | waiting (until next_run_at)
   * | paused (not running right now, e.g. the account was deactivated; resumes automatically)
   */
  state: 'starting' | 'running' | 'waiting' | 'paused';
  interval_minutes: number;
  location: string | null;
  country: string | null;
  industries: string[];
  runs: number;
  last_run_id: string | null;
  /** Campaign (run) ids this schedule has created, oldest first. */
  run_ids: string[];
  created_at: string | null;
  last_run_started_at: string | null;
  last_run_finished_at: string | null;
  next_run_at: string | null;
  /** Error from the most recent run, if it failed (the schedule keeps going). */
  last_error: string | null;
};

export type ContinuousSchedulesResponse = {
  schedules: ContinuousSchedule[];
  count: number;
};

export type RunDraft = {
  lead_id: string;
  email_subject: string;
  email_body: string;
  language: string;
  word_count: number;
  approved: boolean;
  company_name?: string | null;
  /** Lead's contact address the draft goes to (absent on older agents). */
  contact_email?: string | null;
  /** Latest initial-send attempt: sent | failed | bounced | not_sent (absent on older agents). */
  delivery_status?: 'sent' | 'failed' | 'bounced' | 'not_sent' | string;
  sent_at?: string | null;
  receiver_email?: string | null;
  delivery_error?: string | null;
};

export type RunDraftsResponse = {
  pipeline_run_id: string;
  drafts: RunDraft[];
};

export type MeetingsListQuery = {
  status?: string;
  member_id?: string;
  lead_id?: string;
  /** Inclusive ISO dates — fetch only the visible calendar range. */
  date_from?: string;
  date_to?: string;
  limit?: number;
  offset?: number;
};

function buildDiscoveryLeadsQuery(params: DiscoveryLeadsQuery): string {
  const q = new URLSearchParams();
  if (params.city) q.set('city', params.city);
  if (params.industry) q.set('industry', params.industry);
  if (params.company_size) q.set('company_size', params.company_size);
  if (params.min_icp_score != null) q.set('min_icp_score', String(params.min_icp_score));
  if (params.run_id) q.set('run_id', params.run_id);
  if (params.has_email) q.set('has_email', params.has_email);
  if (params.stage) q.set('stage', params.stage);
  q.set('limit', String(params.limit ?? 100));
  q.set('offset', String(params.offset ?? 0));
  const qs = q.toString();
  return qs ? `?${qs}` : '';
}

// ==================== OUTREACH TYPES ====================

export interface OutreachMetricsResponse {
  window_days: number;
  as_of_utc: string;
  volume: {
    total_attempts: number;
    total_sent: number;
    total_failed: number;
    total_replies: number;
  };
  rates: {
    delivery_success_rate: number;
    reply_rate: number;
    positive_reply_rate: number;
    negative_reply_rate: number;
  };
  by_stage: {
    initial: { sent: number; failed: number };
    followup: { sent: number; failed: number };
    reply: { sent: number; failed: number };
    /** Meeting confirmations, reminders, reschedules and cancellations. */
    meeting?: { sent: number; failed: number };
  };
  reply_intents: {
    positive: number;
    neutral: number;
    negative: number;
  };
  lost_reasons: Record<string, number>;
}

export interface ConversationMessage {
  id: string;
  thread_id?: string;
  receiver_email?: string | null;
  /** 'bounce' = a delivery-failure notification from the mail system */
  direction: 'outbound' | 'inbound' | 'bounce' | string;
  subject: string;
  body: string;
  intent?: 'positive' | 'neutral' | 'negative' | string | null;
  sent_at: string;
}

export interface ConversationThreadResponse {
  thread: {
    id: string;
    lead_id: string;
    receiver_email: string;
    company_name: string;
    turn_count: number;
    status: string;
    summary?: string | null;
    bounce?: boolean;
    created_at: string;
    updated_at: string;
  } | null;
  threads?: unknown[];
  messages: ConversationMessage[];
}

export interface ConversationStatsResponse {
  followup: number;
  replied: number;
  in_progress: number;
  meeting_scheduled: number;
  won: number;
  lost: number;
  total_contacted: number;
  total_threads: number;
}

export interface SenderAccount {
  id?: number;
  email_address?: string;
  display_name?: string;
  smtp_host?: string;
  smtp_port?: number;
  smtp_username?: string;
  use_tls?: boolean;
  daily_limit?: number;
  imap_host?: string;
  imap_port?: number;
  imap_username?: string;
  imap_use_ssl?: boolean;
  smtp_password_configured?: boolean;
  imap_password_configured?: boolean;
  configured?: boolean;
}

export interface MeetingHandoff {
  id?: number;
  lead_id?: string;
  company_name?: string;
  prospect_name?: string;
  prospect_email?: string;
  prospect_role?: string;
  assigned_to_name?: string | null;
  assigned_to_email?: string | null;
  assigned_to_role?: string | null;
  preferred_meeting_platform?: string | null;
  confirmed_date?: string | null;
  confirmed_time?: string | null;
  confirmed_timezone?: string | null;
  scheduler_notes?: string | null;
  status?: string;
  created_at?: string;
  updated_at?: string;
}

export interface SentEmail {
  id?: number;
  lead_id?: string;
  company_name?: string;
  /** initial | reply | followup_1 … */
  campaign_stage?: string;
  recipient_email?: string;
  subject?: string;
  body_preview?: string;
  sent_at?: string;
  status?: string;
  error_message?: string | null;
}

export interface SandboxInbox {
  id?: number;
  email?: string;
  created_at?: string;
}

export interface OutreachAccountTestResult {
  ok: boolean;
  message: string;
  smtp?: { ok: boolean; host: string; message: string };
  imap?: { ok: boolean; host: string; derived?: boolean; message: string };
}

export interface TeamMember {
  id: string;
  name: string;
  email: string;
  role?: string | null;
  timezone?: string;
  is_active: boolean;
}

export interface AvailabilitySlot {
  day_of_week: number; // 0=Mon … 6=Sun
  day_name?: string;
  start_time: string;  // "HH:MM" (UTC)
  end_time: string;    // "HH:MM" (UTC)
}

export interface MeetingHandoffsResponse {
  total: number;
  handoffs: MeetingHandoff[];
}

export interface OutreachAccountsResponse {
  accounts: SenderAccount[];
}

export interface SentEmailsResponse {
  /** Count of all sent rows for the user (not just this page). */
  total: number;
  limit?: number;
  offset?: number;
  sent: SentEmail[];
}

export interface SandboxInboxesResponse {
  inboxes: SandboxInbox[];
  total: number;
}

export interface OutreachJobsStatusResponse {
  is_running: boolean;
  sent_today: number;
  /** Health of inbox reading (IMAP). ok === null means not checked yet. */
  reply_tracking?: {
    ok: boolean | null;
    host: string | null;
    user: string | null;
    error: string | null;
    checked_at: string | null;
    /** Recent inbox messages scanned (headers only) on the last check. */
    messages_seen?: number;
    /** Of those, downloaded as possible replies. */
    candidates?: number;
  } | null;
  sender_email: string | null;
  daily_limit: number | null;
}

export const leadGenService = {
  getSettings: async (): Promise<ApiResponse<LeadGenSettingsResponse>> => {
    return apiService.get<LeadGenSettingsResponse>('/customer/lead-gen/settings');
  },

  updateSettings: async (
    payload: LeadGenSettingsPayload,
  ): Promise<ApiResponse<LeadGenSettingsResponse>> => {
    return apiService.put<LeadGenSettingsResponse>('/customer/lead-gen/settings', payload);
  },

  getDiscoveryConfig: async (): Promise<ApiResponse<LeadGenDiscoveryConfigResponse>> => {
    return apiService.get<LeadGenDiscoveryConfigResponse>('/customer/lead-gen/config');
  },

  updateDiscoveryConfig: async (payload: {
    context: GenerateContextPayload;
  }): Promise<ApiResponse<LeadGenDiscoveryConfigResponse>> => {
    return apiService.put<LeadGenDiscoveryConfigResponse>('/customer/lead-gen/config', payload);
  },

  generateLeads: async (payload: { context: GenerateContextPayload }) => {
    return apiService.post('/customer/lead-gen/generate', payload);
  },

  getActiveRun: async (): Promise<ApiResponse<ActiveRunResponse>> => {
    return apiService.get<ActiveRunResponse>('/customer/lead-gen/active-run');
  },

  // ── Continuous discovery schedules ───────────────────────────────────

  getContinuousSchedules: async (): Promise<ApiResponse<ContinuousSchedulesResponse>> => {
    return apiService.get<ContinuousSchedulesResponse>('/customer/lead-gen/continuous');
  },

  updateContinuousInterval: async (
    configId: string,
    intervalMinutes: number,
  ): Promise<ApiResponse<ContinuousSchedule>> => {
    return apiService.patch<ContinuousSchedule>(
      `/customer/lead-gen/continuous/${encodeURIComponent(configId)}`,
      { interval_minutes: intervalMinutes },
    );
  },

  /** Stops the schedule; a run already in progress finishes, no new runs start. */
  stopContinuous: async (configId: string): Promise<ApiResponse<unknown>> => {
    return apiService.delete<unknown>(`/customer/lead-gen/continuous/${encodeURIComponent(configId)}`);
  },

  getDiscoveryLeads: async (
    params: DiscoveryLeadsQuery = {},
  ): Promise<ApiResponse<DiscoveryLeadsApiResponse>> => {
    return apiService.get<DiscoveryLeadsApiResponse>(
      `/customer/lead-gen/discovery/leads${buildDiscoveryLeadsQuery(params)}`,
    );
  },

  getLeadDetail: async (leadId: string): Promise<ApiResponse<unknown>> => {
    return apiService.get<unknown>(`/customer/lead-gen/leads/${leadId}`);
  },

  updateLeadStatus: async (
    leadId: string,
    payload: { status: string; notes?: string },
  ): Promise<ApiResponse<unknown>> => {
    return apiService.patch<unknown>(
      `/customer/lead-gen/leads/${leadId}/status`,
      payload,
    );
  },

  getPipelineRuns: async (
    params: PipelineRunsQuery = {},
  ): Promise<ApiResponse<PipelineRunsResponse>> => {
    const q = new URLSearchParams();
    q.set('limit', String(params.limit ?? 50));
    q.set('offset', String(params.offset ?? 0));
    return apiService.get<PipelineRunsResponse>(
      `/customer/lead-gen/runs?${q.toString()}`,
    );
  },

  getRunDrafts: async (runId: string): Promise<ApiResponse<RunDraftsResponse>> => {
    return apiService.get<RunDraftsResponse>(`/customer/lead-gen/runs/${runId}/drafts`);
  },

  /** Re-scrape websites of leads in a campaign that have no email; auto-sends when found. */
  findRunContacts: async (runId: string): Promise<ApiResponse<FindContactsStatus>> => {
    return apiService.post<FindContactsStatus>(`/customer/lead-gen/runs/${runId}/find-contacts`, {});
  },

  getFindContactsStatus: async (runId: string): Promise<ApiResponse<FindContactsStatus>> => {
    return apiService.get<FindContactsStatus>(`/customer/lead-gen/runs/${runId}/find-contacts`);
  },

  /** Attach an email to a lead; the drafted email is sent immediately if the lead was never contacted. */
  updateLeadContact: async (
    leadId: string,
    contactEmail: string,
  ): Promise<ApiResponse<LeadContactUpdateResult>> => {
    return apiService.patch<LeadContactUpdateResult>(`/customer/lead-gen/leads/${leadId}/contact`, {
      contact_email: contactEmail,
    });
  },

  getPipelineLeads: async (
    params: PipelineLeadsQuery = {},
  ): Promise<ApiResponse<PipelineLeadsApiResponse>> => {
    const q = new URLSearchParams();
    q.set('limit', String(params.limit ?? 200));
    if (params.run_id) q.set('run_id', params.run_id);
    const qs = q.toString();
    return apiService.get<PipelineLeadsApiResponse>(
      `/customer/lead-gen/pipeline/leads${qs ? `?${qs}` : ''}`,
    );
  },

  getMeetings: async (
    params: MeetingsListQuery = {},
  ): Promise<ApiResponse<MeetingsListApiResponse>> => {
    const q = new URLSearchParams();
    if (params.status) q.set('status', params.status);
    if (params.member_id) q.set('member_id', params.member_id);
    if (params.lead_id) q.set('lead_id', params.lead_id);
    if (params.date_from) q.set('date_from', params.date_from);
    if (params.date_to) q.set('date_to', params.date_to);
    if (params.offset) q.set('offset', String(params.offset));
    q.set('limit', String(params.limit ?? 50));
    const qs = q.toString();
    return apiService.get<MeetingsListApiResponse>(
      `/customer/lead-gen/meetings${qs ? `?${qs}` : ''}`,
    );
  },

  getMeeting: async (meetingId: string): Promise<ApiResponse<MeetingApi>> => {
    return apiService.get<MeetingApi>(`/customer/lead-gen/meetings/${meetingId}`);
  },

  confirmMeeting: async (meetingId: string): Promise<ApiResponse<unknown>> => {
    return apiService.post<unknown>(`/customer/lead-gen/meetings/${meetingId}/confirm`);
  },

  /** Move a meeting: re-assigns for the new slot, re-confirms and emails prospect + assignee. */
  rescheduleMeeting: async (
    meetingId: string,
    payload: { scheduled_date: string; scheduled_time: string; timezone?: string },
  ): Promise<ApiResponse<MeetingApi>> => {
    return apiService.post<MeetingApi>(`/customer/lead-gen/meetings/${meetingId}/reschedule`, payload);
  },

  cancelMeeting: async (
    meetingId: string,
    payload: { reason?: string; notify_prospect?: boolean } = {},
  ): Promise<ApiResponse<MeetingApi>> => {
    return apiService.post<MeetingApi>(`/customer/lead-gen/meetings/${meetingId}/cancel`, payload);
  },

  /** Post-meeting outcome; status inferred (won/lost) by the agent when omitted. Marks the meeting completed. */
  addMeetingOutcome: async (
    meetingId: string,
    payload: { outcome_note: string; outcome_status?: 'won' | 'lost' },
  ): Promise<ApiResponse<unknown>> => {
    return apiService.post<unknown>(`/customer/lead-gen/meetings/${meetingId}/outcome`, payload);
  },

  updateMeeting: async (
    meetingId: string,
    payload: Partial<{
      assigned_member_id: string | null;
      scheduled_date: string;
      scheduled_time: string;
      timezone: string;
      duration_minutes: number;
      platform: string;
      meeting_link: string;
      status: string;
      cancellation_reason: string;
      agenda: string;
      notes: string;
    }>,
  ): Promise<ApiResponse<MeetingApi>> => {
    return apiService.patch<MeetingApi>(`/customer/lead-gen/meetings/${meetingId}`, payload);
  },

  // ==================== TEAM MEMBERS & AVAILABILITY ====================

  listTeamMembers: async (): Promise<ApiResponse<{ members: TeamMember[] }>> => {
    return apiService.get<{ members: TeamMember[] }>('/customer/lead-gen/meetings/members');
  },

  addTeamMember: async (
    payload: { name: string; email: string; role?: string; timezone?: string },
  ): Promise<ApiResponse<TeamMember>> => {
    return apiService.post<TeamMember>('/customer/lead-gen/meetings/members', payload);
  },

  updateTeamMember: async (
    memberId: string,
    payload: Partial<{ name: string; email: string; role: string; timezone: string; is_active: boolean }>,
  ): Promise<ApiResponse<TeamMember>> => {
    return apiService.patch<TeamMember>(`/customer/lead-gen/meetings/members/${memberId}`, payload);
  },

  deactivateTeamMember: async (memberId: string): Promise<ApiResponse<unknown>> => {
    return apiService.delete<unknown>(`/customer/lead-gen/meetings/members/${memberId}`);
  },

  getMemberAvailability: async (
    memberId: string,
  ): Promise<ApiResponse<{ member_id: string; slots: AvailabilitySlot[] }>> => {
    return apiService.get<{ member_id: string; slots: AvailabilitySlot[] }>(
      `/customer/lead-gen/meetings/members/${memberId}/availability`,
    );
  },

  setMemberAvailability: async (
    memberId: string,
    slots: Array<{ day_of_week: number; start_time: string; end_time: string }>,
  ): Promise<ApiResponse<unknown>> => {
    return apiService.put<unknown>(`/customer/lead-gen/meetings/members/${memberId}/availability`, { slots });
  },

  // ==================== OUTREACH ENDPOINTS ====================

  getOutreachMetrics: async (): Promise<ApiResponse<OutreachMetricsResponse>> => {
    return apiService.get<OutreachMetricsResponse>('/customer/lead-gen/outreach/metrics');
  },

  getConversationStats: async (): Promise<ApiResponse<ConversationStatsResponse>> => {
    return apiService.get<ConversationStatsResponse>('/customer/lead-gen/outreach/conversation-stats');
  },

  getConversationThread: async (leadId: string): Promise<ApiResponse<ConversationThreadResponse>> => {
    return apiService.get<ConversationThreadResponse>(`/customer/lead-gen/outreach/conversations/${leadId}`);
  },

  getMeetingHandoffs: async (
    params?: Record<string, string>,
  ): Promise<ApiResponse<MeetingHandoffsResponse>> => {
    const q = params ? `?${new URLSearchParams(params).toString()}` : '';
    return apiService.get<MeetingHandoffsResponse>(
      `/customer/lead-gen/outreach/meeting-handoffs${q}`,
    );
  },

  updateMeetingHandoff: async (
    id: number,
    payload: Partial<{
      assigned_to_name: string;
      assigned_to_email: string;
      assigned_to_role: string;
      preferred_meeting_platform: string;
      confirmed_date: string;
      confirmed_time: string;
      confirmed_timezone: string;
      scheduler_notes: string;
      status: string;
    }>,
  ): Promise<ApiResponse<unknown>> => {
    return apiService.patch<unknown>(
      `/customer/lead-gen/outreach/meeting-handoffs/${id}`,
      payload,
    );
  },

  // ── Primary Sender Account (singular) ─────────────────────────────────

  getPrimaryOutreachAccount: async (): Promise<ApiResponse<SenderAccount>> => {
    return apiService.get<SenderAccount>('/customer/lead-gen/outreach/account');
  },

  upsertPrimaryOutreachAccount: async (
    payload: Partial<{
      email_address: string;
      display_name: string;
      smtp_host: string;
      smtp_port: number;
      smtp_username: string;
      smtp_password: string;
      use_tls: boolean;
      daily_limit: number;
      imap_host: string;
      imap_port: number;
      imap_username: string;
      imap_password: string;
      imap_use_ssl: boolean;
    }>,
  ): Promise<ApiResponse<SenderAccount>> => {
    return apiService.put<SenderAccount>('/customer/lead-gen/outreach/account', payload);
  },

  testOutreachAccount: async (): Promise<ApiResponse<OutreachAccountTestResult>> => {
    return apiService.post<OutreachAccountTestResult>('/customer/lead-gen/outreach/account/test');
  },

  // ── Multiple Sender Accounts (plural) ─────────────────────────────────

  getOutreachAccounts: async (): Promise<ApiResponse<OutreachAccountsResponse>> => {
    return apiService.get<OutreachAccountsResponse>('/customer/lead-gen/outreach/accounts');
  },

  addOutreachAccount: async (
    payload: Partial<{
      email_address: string;
      display_name: string;
      smtp_host: string;
      smtp_port: number;
      smtp_username: string;
      smtp_password: string;
      use_tls: boolean;
      daily_limit: number;
      imap_host: string;
      imap_port: number;
      imap_username: string;
      imap_password: string;
      imap_use_ssl: boolean;
    }>,
  ): Promise<ApiResponse<unknown>> => {
    return apiService.post<unknown>('/customer/lead-gen/outreach/accounts', payload);
  },

  deactivateOutreachAccount: async (
    accountId: string,
  ): Promise<ApiResponse<unknown>> => {
    return apiService.delete<unknown>(
      `/customer/lead-gen/outreach/accounts/${accountId}`,
    );
  },

  // ── Sent Log ──────────────────────────────────────────────────────────

  getSentEmails: async (
    params?: { limit?: number; offset?: number },
  ): Promise<ApiResponse<SentEmailsResponse>> => {
    const q = new URLSearchParams();
    q.set('limit', String(params?.limit ?? 50));
    if (params?.offset) q.set('offset', String(params.offset));
    return apiService.get<SentEmailsResponse>(
      `/customer/lead-gen/outreach/sent?${q.toString()}`,
    );
  },

  // ── Sandbox ───────────────────────────────────────────────────────────

  getSandboxInboxes: async (): Promise<ApiResponse<SandboxInboxesResponse>> => {
    return apiService.get<SandboxInboxesResponse>('/customer/lead-gen/outreach/sandbox/inboxes');
  },

  replaceSandboxInboxes: async (
    payload: { emails: string[] },
  ): Promise<ApiResponse<unknown>> => {
    return apiService.put<unknown>('/customer/lead-gen/outreach/sandbox/inboxes', payload);
  },

  deleteSandboxInbox: async (inboxId: string): Promise<ApiResponse<unknown>> => {
    return apiService.delete<unknown>(
      `/customer/lead-gen/outreach/sandbox/inboxes/${inboxId}`,
    );
  },

  clearLeadRecipientMap: async (): Promise<ApiResponse<unknown>> => {
    return apiService.delete<unknown>(
      '/customer/lead-gen/outreach/sandbox/lead-recipient-map',
    );
  },

  // ── Outreach Jobs (Continuous Campaign) ───────────────────────────────

  getOutreachJobsStatus: async (): Promise<ApiResponse<OutreachJobsStatusResponse>> => {
    return apiService.get<OutreachJobsStatusResponse>('/customer/lead-gen/outreach/jobs/status');
  },

  startOutreachJob: async (
    payload?: { interval_minutes?: number },
  ): Promise<ApiResponse<unknown>> => {
    return apiService.post<unknown>('/customer/lead-gen/outreach/jobs/start', payload ?? {});
  },

  stopOutreachJob: async (): Promise<ApiResponse<unknown>> => {
    return apiService.delete<unknown>('/customer/lead-gen/outreach/jobs/stop');
  },

  // ── Manual Actions ────────────────────────────────────────────────────

  runOutreachNow: async (): Promise<ApiResponse<unknown>> => {
    return apiService.post<unknown>('/customer/lead-gen/outreach/run-now');
  },

  sendOutreachLead: async (
    payload: { lead_id: string; receiver_email?: string },
  ): Promise<ApiResponse<unknown>> => {
    return apiService.post<unknown>('/customer/lead-gen/outreach/send-lead', payload);
  },

  sendOutreachByIndustry: async (
    payload: { run_id: string; industry: string },
  ): Promise<ApiResponse<unknown>> => {
    return apiService.post<unknown>('/customer/lead-gen/outreach/send-by-industry', payload);
  },
};

export default leadGenService;
